import { DialogTitle } from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { isString } from 'lodash-es';
import type { FunctionComponent, ReactNode } from 'react';
import { useId, useMemo, useState } from 'react';
import { ModalLegacy } from './modal-legacy';
import { Title } from './title';

const defaultProps = {};

type DialogOptions<T extends Record<string, unknown> = Record<string, unknown>> = {
  title: FunctionComponent | string;
  content?: FunctionComponent<Partial<T>> | string | null;
  buttons: FunctionComponent<Partial<T> & { closeDialog: () => void }>;
};

export function useDialog<T extends Record<string, unknown>>({ title, content = null, buttons }: DialogOptions<T>) {
  const htmlId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [props, setProps] = useState<Partial<T>>(defaultProps);

  return useMemo(() => {
    const openDialog = (props: T = defaultProps as T) => {
      setIsOpen(true);
      setProps(props);
    };

    const closeDialog = () => {
      setIsOpen(false);
      setProps(defaultProps);
    };

    const dialog = isOpen ? (
      <ModalLegacy isOpen={isOpen} onClose={closeDialog} className="w-1/3">
        <section className="p-6" aria-labelledby={htmlId}>
          <Title className="mb-5" id={htmlId}>{isString(title) ? title : (title({}) as ReactNode)}</Title>
          <VisuallyHidden>
            <DialogTitle>{isString(title) ? title : (title({}) as ReactNode)}</DialogTitle>
          </VisuallyHidden>
          {isString(content) ? <p>{content}</p> : (content?.(props) as ReactNode)}
          <div className="flex flex-row-reverse flex-wrap mt-5 gap-2">
            {(buttons({ ...props, closeDialog }) as ReactNode)}
          </div>
        </section>
      </ModalLegacy>
    ) : null;

    return [
      dialog,
      openDialog,
      closeDialog,
    ] as const;
  }, [isOpen, htmlId, title, content, props, buttons]);
}
